/** Coerce API decimal strings (e.g. "4.00") or numbers to a finite number. */
export function toFeeNumber(value) {
  const n = parseFloat(String(value ?? 0));
  return Number.isFinite(n) ? n : 0;
}

export const DEFAULT_PROCESSING_FEE_PERCENT = 4;
export const DEFAULT_SALES_TAX_PERCENT = 7;

/** Read museum-level fee/tax % (camelCase or snake_case); default 4 / 7 when absent. */
export function getMuseumFeeTaxPercents(museum) {
  const feeRaw = museum?.processingFeePercent ?? museum?.processing_fee_percent;
  const taxRaw = museum?.salesTaxPercent ?? museum?.sales_tax_percent;

  return {
    processingFeePercent:
      feeRaw == null || feeRaw === ""
        ? DEFAULT_PROCESSING_FEE_PERCENT
        : toFeeNumber(feeRaw),
    salesTaxPercent:
      taxRaw == null || taxRaw === ""
        ? DEFAULT_SALES_TAX_PERCENT
        : toFeeNumber(taxRaw),
  };
}

/**
 * Museum-level fee/tax applied to a cart/order subtotal.
 * fee = subtotal × (processingFeePercent / 100)
 * tax = subtotal × (salesTaxPercent / 100)
 * total = subtotal + fee + tax
 */
export function calculateFeeTaxTotals(
  basePrice,
  processingFeePercent = DEFAULT_PROCESSING_FEE_PERCENT,
  salesTaxPercent = DEFAULT_SALES_TAX_PERCENT
) {
  const subtotal = toFeeNumber(basePrice);
  const feePercent =
    processingFeePercent == null || processingFeePercent === ""
      ? DEFAULT_PROCESSING_FEE_PERCENT
      : toFeeNumber(processingFeePercent);
  const taxPercent =
    salesTaxPercent == null || salesTaxPercent === ""
      ? DEFAULT_SALES_TAX_PERCENT
      : toFeeNumber(salesTaxPercent);
  const processingFee = subtotal * (feePercent / 100);
  const salesTax = subtotal * (taxPercent / 100);

  return {
    subtotal,
    processingFee,
    salesTax,
    total: subtotal + processingFee + salesTax,
    processingFeePercent: feePercent,
    salesTaxPercent: taxPercent,
  };
}

/** Round money to cents for API payloads / display math. */
export function roundMoney(value) {
  return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
}

export function calculateMuseumFeeTaxTotals(subtotal, museum) {
  const { processingFeePercent, salesTaxPercent } =
    getMuseumFeeTaxPercents(museum);
  const breakdown = calculateFeeTaxTotals(
    subtotal,
    processingFeePercent,
    salesTaxPercent
  );
  return {
    ...breakdown,
    subtotal: roundMoney(breakdown.subtotal),
    processingFee: roundMoney(breakdown.processingFee),
    salesTax: roundMoney(breakdown.salesTax),
    total: roundMoney(breakdown.total),
  };
}
